import { Component, OnInit, Injector, HostListener, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { scheduleStatus } from 'app/core/interfaces/schedule';
import { scheduleFilter } from 'app/core/data/forms.json';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { tabI } from 'app/core/interfaces/layout';
import { Encounter, EncounterListResult } from 'app/core/interfaces/encounter';
import { EncounterService } from 'app/core/http/services/encounter.service';
import { getPaymentStatusText } from 'app/core/utils/payment-status';
import { OrganizationScheduleDetailsComponent } from './details/schedule-details.component';
import Schedule from 'app/core/models/Schedule';
import { ScheduleOrganizationEditStatusComponent } from './edit-status/schedule-edit-status.component';
import { Subject, Subscription } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { MatTabChangeEvent } from '@angular/material/tabs';

@Component({
  selector: 'page-schedule-status',
  templateUrl: './schedule-status.component.html',
  styleUrls: ['./schedule-status.component.scss'],
})
export class OrganizationScheduleStatusComponent extends BaseComponent implements OnInit, OnDestroy {
  public title = 'Status de atendimento';

  public scheduleStatus: scheduleStatus[] = [
    { id: 'planned', text: 'Realização pendente', color: 'red', icon: 'error' },
    { id: 'finished', text: 'Confirmado', color: 'blue', icon: 'check_circle' },
    { id: 'in-progress', text: 'Em atendimento', color: 'orange', icon: 'pending' },
    { id: 'isByOrder', text: 'Ordem de chegada', color: 'gray', icon: 'error' },
    { id: 'cancelled', text: 'Cancelado', color: 'red', icon: 'error' },
  ];

  public encounterType = 0;
  public buscarScheduleControl = new FormControl('');
  public filterTipos = scheduleFilter;
  public tabs: tabI[] = [];
  public activeTabId = 1;

  public encounters: Encounter[] = [];
  public schedules: Schedule[] = [];
  public organizationLogged: any = {};

  public itemsPerPage = 10;
  public currentPage: { [key: number]: number } = {};

  // Listas prontas. Não use getter filtrando array no template, pois isso causa re-render no scroll.
  public atendimentosFiltroList: Encounter[] = [];
  public atendimentosEmAbertoList: Encounter[] = [];
  public atendimentosFinalizadosList: Encounter[] = [];

  // Autocomplete
  public showAutocompleteHint = false;
  public autocompleteSuggestions: string[] = [];
  public showAutocompletePanel = false;
  public autocompleteLoading = false;

  // Paginação
  public total = 0;
  public lastEvaluateKey: any | null = null;
  public pageSize = 25;
  public hasMore = true;
  public loadingInitial = false;
  public loadingMore = false;
  public loadError = false;
  public appliedSearchTerm = '';
  public readonly minSearchLength = 2;

  private listSub?: Subscription;
  private destroy$ = new Subject<void>();

  // Controle separado para requisição. Não use this.loading no loadMore,
  // porque ele estava afetando botões/cards do template.
  private requestInFlight = false;

  // Controle do auto-load por scroll.
  // Carrega quando chega perto do final, mas só dispara novamente
  // depois que o usuário continuar descendo a página.
  private scrollTicking = false;
  private lastLoadMoreAt = 0;
  private lastAutoLoadScrollTop = -1;
  private readonly loadMoreCooldownMs = 900;
  private readonly autoLoadDistancePx = 550;
  private readonly minScrollDeltaAfterAutoLoadPx = 80;

  private readonly onAnyScroll = (event: Event) => {
    if (this.scrollTicking) return;

    this.scrollTicking = true;

    requestAnimationFrame(() => {
      this.scrollTicking = false;
      this.handleScrollNearBottom(event.target);
    });
  };

  constructor(
    private injector: Injector,
    private encounterService: EncounterService,
  ) {
    super(injector);
    this.initTabs();
  }

  ngOnInit(): void {
    const user = this.getUserLogged();

    this.organizationLogged = {
      organization_id: user.id,
      name: user.name,
      email: user.email,
    };

    this.setupAutocomplete();
    this.attachScrollListeners();
    this.applyFilters(true);
  }

  ngOnDestroy(): void {
    this.detachScrollListeners();
    this.listSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupAutocomplete(): void {
    this.buscarScheduleControl.valueChanges.pipe(debounceTime(250), distinctUntilChanged()).subscribe((searchTerm: any) => {
      const raw = String(searchTerm ?? '');
      const term = raw.trim();

      this.updateAutocompleteSuggestions(term);

      if (term.length === 0) {
        this.applyFilters(true, '');
        return;
      }

      if (term.length < this.minSearchLength) {
        if (this.appliedSearchTerm) {
          this.applyFilters(true, '');
        } else {
          this.updateFilteredLists();
        }
        return;
      }

      this.applyFilters(true, term.toLowerCase());
    });
  }

  updateAutocompleteSuggestions(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 3) {
      this.autocompleteSuggestions = [];
      this.showAutocompletePanel = false;
      this.showAutocompleteHint = searchTerm.length > 0 && searchTerm.length < 3;
      return;
    }

    const term = searchTerm.toLowerCase();
    const suggestions = new Set<string>();

    this.encounters.forEach((encounter) => {
      if (encounter.patient?.name && encounter.patient.name.toLowerCase().includes(term)) {
        suggestions.add(encounter.patient.name);
      }

      if (encounter.practitioner?.name && encounter.practitioner.name.toLowerCase().includes(term)) {
        suggestions.add(encounter.practitioner.name);
      }

      if (encounter.name && encounter.name.toLowerCase().includes(term)) {
        suggestions.add(encounter.name);
      }
    });

    this.autocompleteSuggestions = Array.from(suggestions).slice(0, 5);
    this.showAutocompletePanel = this.autocompleteSuggestions.length > 0;
    this.showAutocompleteHint = false;
  }

  selectSuggestion(suggestion: string): void {
    this.buscarScheduleControl.setValue(suggestion, { emitEvent: false });
    this.showAutocompletePanel = false;
    this.applyFilters(true, suggestion);
  }

  onInputFocus(): void {
    const value = String(this.buscarScheduleControl.value ?? '');

    if (value.length >= 3) {
      this.updateAutocompleteSuggestions(value);
    }
  }

  onInputBlur(): void {
    setTimeout(() => {
      this.showAutocompletePanel = false;
    }, 150);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const inputElement = document.querySelector('input[matInput]');
    const panelElement = document.querySelector('.autocomplete-panel');

    if (inputElement && panelElement) {
      const clickedInsideInput = inputElement.contains(event.target as Node);
      const clickedInsidePanel = panelElement.contains(event.target as Node);

      if (!clickedInsideInput && !clickedInsidePanel) {
        this.showAutocompletePanel = false;
      }
    }
  }

  public get buscarSchedule(): string {
    return String(this.buscarScheduleControl.value ?? '');
  }

  public get scheduleFiltro(): Schedule[] {
    return this.schedules;
  }

  // Mantidos para compatibilidade com outras partes do componente.
  public get atendimentosFiltro(): Encounter[] {
    return this.atendimentosFiltroList;
  }

  public get atendimentosFinalizados(): Encounter[] {
    return this.atendimentosFinalizadosList;
  }

  public get atendimentosEmAberto(): Encounter[] {
    return this.atendimentosEmAbertoList;
  }

  private applyFilters(reset: boolean, termOverride?: string): void {
    const raw = termOverride !== undefined ? termOverride : String(this.buscarScheduleControl.value ?? '').trim();
    this.appliedSearchTerm = raw.length >= this.minSearchLength ? raw.toLowerCase() : '';
    this.getEncounters(reset, this.appliedSearchTerm);
  }

  public onTipoChange(): void {
    this.updateFilteredLists();
    this.resetAutoLoadGate();
  }

  openComponentDialogScheduleDetailForm(encounter: Encounter): void {
    const data = {
      title: `Dados do atendimento - ${encounter.encounter_id}`,
      param: {
        encounterFHIR: this.selectEncounterFHIR(encounter),
        encounter,
      },
    };

    this.dialog.openComponentDialog(OrganizationScheduleDetailsComponent, data);
  }

  getStatusPaymentText(statusPayment?: string): string {
    return getPaymentStatusText(statusPayment);
  }

  editStatus(encounter: Encounter): void {
    this.dialog
      .openSmallComponentDialog(ScheduleOrganizationEditStatusComponent, {
        title: `Alterar status - ${encounter.encounter_id}`,
        param: {
          encounterFHIR: this.selectEncounterFHIR(encounter),
          encounter,
        },
      })
      .subscribe({
        next: (updatedEncounter?: Encounter) => {
          if (updatedEncounter) {
            this.replaceEncounter(updatedEncounter);
          }
        },
      });
  }

  private replaceEncounter(updatedEncounter: Encounter): void {
    this.encounters = this.encounters.map((encounter) => (encounter.encounter_id === updatedEncounter.encounter_id ? updatedEncounter : encounter));
    this.updateFilteredLists();
  }

  getTextStatus(statusCode: any, isByOrder?: boolean): string | undefined {
    const adjustedStatusCode = isByOrder && statusCode === 'planned' ? 'isByOrder' : statusCode;
    return this.scheduleStatus.find((item) => item.id === adjustedStatusCode)?.text;
  }

  getColorStatus(statusCode: any, isByOrder?: boolean, statusPayment?: string): string | undefined {
    const adjustedStatusCode: any = isByOrder && statusCode === 'planned' ? 'isByOrder' : statusCode;
    if (adjustedStatusCode === 'planned' && statusPayment === 'PAID') return 'green';
    if (adjustedStatusCode === 'finished') return 'blue';
    return this.scheduleStatus.find((item) => item.id === adjustedStatusCode)?.color;
  }

  private buildParams(loadMore: boolean, search: string): HttpParams {
    let params = new HttpParams().set('page-size', String(this.pageSize));

    if (search) {
      params = params.set('search', search);
    }

    if (loadMore && this.lastEvaluateKey) {
      params = params.set('last-evaluated-key', this.serializeKey(this.lastEvaluateKey));
    }

    return params;
  }

  public getEncounters(reset: boolean = true, search: string = ''): void {
    if (this.requestInFlight) {
      if (!reset) return;

      // Em nova busca/filtro, cancela a chamada anterior para evitar resposta antiga redesenhando a tela.
      this.listSub?.unsubscribe();
      this.finishLoading();
    }

    const previousKey = this.lastEvaluateKey;

    if (reset) {
      this.encounters = [];
      this.total = 0;
      this.lastEvaluateKey = null;
      this.hasMore = true;
      this.loadError = false;
      this.lastLoadMoreAt = 0;
      this.resetAutoLoadGate();
      this.updateFilteredLists();
    }

    this.requestInFlight = true;
    this.loadingInitial = reset;
    this.loadingMore = !reset;

    // Importante: loading global só no carregamento inicial.
    // No loadMore ele causava pisca/desabilitação visual dos botões dos cards.
    this.loading = reset;

    const params = this.buildParams(!reset, search);

    this.listSub = this.encounterService.listByOrganizationNewPaged(this.organizationLogged.organization_id, params).subscribe({
      next: (res: EncounterListResult) => {
        const items = res.items ?? [];
        const newKey = res.lastEvaluatedKey ?? null;
        const sameKey = !reset && !!newKey && this.serializeKey(newKey) === this.serializeKey(previousKey);

        this.encounters = reset ? items : this.mergeUniqueEncounters(this.encounters, items);
        this.total = res.count ?? this.total;
        this.lastEvaluateKey = newKey;
        this.hasMore = !!newKey && !sameKey && items.length > 0;

        this.updateFilteredLists();
        this.finishLoading();
      },
      error: (err) => {
        console.log('error buscar encounter', err);
        this.loadError = true;
        this.finishLoading();
      },
    });
  }

  private mergeUniqueEncounters(current: Encounter[], incoming: Encounter[]): Encounter[] {
    const map = new Map<string, Encounter>();

    [...current, ...incoming].forEach((item, index) => {
      const key = item.encounter_id || `${item.startDate}-${item.name}-${index}`;
      map.set(key, item);
    });

    return Array.from(map.values());
  }

  private updateFilteredLists(): void {
    const rawSearch = String(this.buscarSchedule ?? '').trim();
    const search = rawSearch.length >= this.minSearchLength ? rawSearch.toUpperCase() : '';
    const hasSearch = search.length > 0;

    this.atendimentosFiltroList = this.encounters.filter((item: Encounter) => {
      const organizationName = item.organization?.name?.toUpperCase() ?? '';
      const encounterName = item.name?.toUpperCase() ?? '';
      const patientName = item.patient?.name?.toUpperCase() ?? '';
      const practitionerName = item.practitioner?.name?.toUpperCase() ?? '';

      const matchesSearch =
        !hasSearch ||
        organizationName.includes(search) ||
        encounterName.includes(search) ||
        patientName.includes(search) ||
        practitionerName.includes(search);

      const matchesTipo =
        this.encounterType === 0 || (this.encounterType === 1 && item.type === 'Consulta') || (this.encounterType === 2 && item.type === 'Exame');

      return matchesSearch && matchesTipo;
    });

    this.atendimentosEmAbertoList = this.atendimentosFiltroList.filter(
      (item: Encounter) => item.status === 'planned' || item.status === 'in-progress',
    );

    this.atendimentosFinalizadosList = this.atendimentosFiltroList.filter(
      (item: Encounter) => item.status === 'cancelled' || item.status === 'finished',
    );
  }

  private finishLoading(): void {
    this.requestInFlight = false;
    this.loading = false;
    this.loadingInitial = false;
    this.loadingMore = false;
    this.autocompleteLoading = false;
  }

  public loadMore(fromAutoScroll = false, currentScrollTop?: number): void {
    if (this.requestInFlight || !this.hasMore) return;

    const now = Date.now();
    if (now - this.lastLoadMoreAt < this.loadMoreCooldownMs) return;

    this.lastLoadMoreAt = now;

    if (fromAutoScroll && typeof currentScrollTop === 'number') {
      this.lastAutoLoadScrollTop = currentScrollTop;
    }

    this.getEncounters(false, this.appliedSearchTerm);
  }

  public retry(): void {
    this.applyFilters(true);
  }

  public onTabChange(ev: MatTabChangeEvent): void {
    this.activeTabId = this.tabs[ev.index]?.id ?? 1;
    this.resetAutoLoadGate();
  }

  selectEncounterFHIR(encounterSelected: Encounter): Encounter | undefined {
    return this.encounters.find((e) => e.encounter_id === encounterSelected.encounter_id);
  }

  getTotalPages(tabId: number): number {
    const total = tabId === 1 ? this.atendimentosEmAbertoList.length : this.atendimentosFinalizadosList.length;
    return Math.ceil(total / this.itemsPerPage);
  }

  getContent(tabId: number): Encounter[] {
    return tabId === 1 ? this.atendimentosEmAbertoList : this.atendimentosFinalizadosList;
  }

  getContentLength(tabId: number): number {
    return tabId === 1 ? this.atendimentosEmAbertoList.length : this.atendimentosFinalizadosList.length;
  }

  changePage(tabId: number, direction: 'next' | 'prev'): void {
    const totalItems = tabId === 1 ? this.atendimentosEmAbertoList.length : this.atendimentosFinalizadosList.length;
    const totalPages = Math.ceil(totalItems / this.itemsPerPage);
    const current = this.currentPage[tabId] ?? 1;

    if (direction === 'next' && current < totalPages) {
      this.currentPage[tabId] = current + 1;
    }

    if (direction === 'prev' && current > 1) {
      this.currentPage[tabId] = current - 1;
    }
  }

  initTabs(): void {
    this.tabs = [
      { id: 1, title: 'Agendados', type: 'encounter' },
      { id: 2, title: 'Finalizados', type: 'encounter' },
    ];
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackBySuggestion(index: number, suggestion: string): string {
    return suggestion;
  }

  trackByTab(index: number, tab: tabI): number {
    return tab.id;
  }

  trackByEncounter(index: number, encounter: Encounter): string {
    return encounter.encounter_id || `${encounter.startDate}-${encounter.name}-${index}`;
  }

  private attachScrollListeners(): void {
    window.addEventListener('scroll', this.onAnyScroll, { passive: true });
    document.addEventListener('scroll', this.onAnyScroll, { passive: true, capture: true });
  }

  private detachScrollListeners(): void {
    window.removeEventListener('scroll', this.onAnyScroll);
    document.removeEventListener('scroll', this.onAnyScroll, true);
  }

  private handleScrollNearBottom(target: EventTarget | null): void {
    if (this.requestInFlight || !this.hasMore || this.loadingInitial) return;

    const metrics = this.getScrollMetrics(target);
    if (!metrics || metrics.scrollHeight <= metrics.clientHeight) return;

    const distanceToBottom = metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight;
    if (distanceToBottom > this.autoLoadDistancePx) return;

    const userContinuedScrolling =
      this.lastAutoLoadScrollTop < 0 || metrics.scrollTop >= this.lastAutoLoadScrollTop + this.minScrollDeltaAfterAutoLoadPx;

    if (userContinuedScrolling) {
      this.loadMore(true, metrics.scrollTop);
    }
  }

  private getScrollMetrics(target: EventTarget | null): { scrollTop: number; scrollHeight: number; clientHeight: number } | null {
    const documentElement = document.scrollingElement || document.documentElement;

    if (!target || target === document || target === window) {
      return {
        scrollTop: documentElement.scrollTop || window.scrollY || 0,
        scrollHeight: documentElement.scrollHeight,
        clientHeight: window.innerHeight || documentElement.clientHeight,
      };
    }

    const element = target as HTMLElement;

    if (typeof element.scrollTop === 'number' && element.scrollHeight && element.clientHeight) {
      return {
        scrollTop: element.scrollTop,
        scrollHeight: element.scrollHeight,
        clientHeight: element.clientHeight,
      };
    }

    return {
      scrollTop: documentElement.scrollTop || window.scrollY || 0,
      scrollHeight: documentElement.scrollHeight,
      clientHeight: window.innerHeight || documentElement.clientHeight,
    };
  }

  private resetAutoLoadGate(): void {
    this.lastAutoLoadScrollTop = -1;
  }

  private serializeKey(key: any): string {
    if (!key) return '';
    return typeof key === 'string' ? key : JSON.stringify(key);
  }
}
